import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, IconButton, TextField, Tooltip, Typography, Chip, Stack } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { assistantChat, assistantStatus } from '../../services/aiApi';

const MAX_CONTENT = 6000;
const EASE = [0.2, 0, 0, 1];
const SOUND_KEY = 'hr_assistant_sound_v1';

const STARTERS = ['My leave balance', "What's pending for me", 'Company holidays', 'How do I apply for leave'];

const GREETING = () => ({
  id: 0,
  role: 'assistant',
  content:
    "Hi! I'm your HR assistant. I can help you check your leave balance, review your profile, "
    + "see what's pending for you, answer questions about company HR policies, and help you use the HR system. "
    + 'What would you like to know?',
  chips: STARTERS,
  at: null,
});

/* Synthesized UI sounds — Web Audio API, no asset files, fully local & muteable. */
function useSound() {
  const ctxRef = useRef(null);
  const [muted, setMuted] = useState(() => localStorage.getItem(SOUND_KEY) === 'off');

  const ensure = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    const ctx = ctxRef.current;
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  }, []);

  const tone = useCallback((freqs, { type = 'sine', dur = 0.12, gain = 0.04, step = 0.06 } = {}) => {
    const ctx = ensure();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      const start = t0 + i * step;
      o.type = type;
      o.frequency.setValueAtTime(f, start);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.linearRampToValueAtTime(gain, start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(start); o.stop(start + dur + 0.03);
    });
  }, [ensure]);

  const play = useCallback((name) => {
    if (muted) return;
    try {
      switch (name) {
        case 'open':    tone([440, 587], { dur: 0.14, gain: 0.035 }); break;
        case 'send':    tone([523], { type: 'triangle', dur: 0.07, gain: 0.03 }); break;
        case 'receive': tone([659, 880], { dur: 0.12, gain: 0.03 }); break;
        case 'error':   tone([220, 175], { type: 'sawtooth', dur: 0.16, gain: 0.025 }); break;
        default: break;
      }
    } catch { /* best-effort */ }
  }, [muted, tone]);

  const toggle = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem(SOUND_KEY, next ? 'off' : 'on');
      if (!next) ensure();
      return next;
    });
  }, [ensure]);

  return { play, muted, toggle };
}

/** Minimal **bold** + line-break renderer (no markdown dependency). */
function RichText({ text }) {
  const nodes = useMemo(() => {
    const out = [];
    (text || '').split('\n').forEach((line, li) => {
      if (li > 0) out.push(<br key={`br-${li}`} />);
      line.split(/(\*\*[^*]+\*\*)/g).forEach((p, pi) => {
        if (/^\*\*[^*]+\*\*$/.test(p)) out.push(<strong key={`b-${li}-${pi}`}>{p.slice(2, -2)}</strong>);
        else if (p) out.push(<React.Fragment key={`t-${li}-${pi}`}>{p}</React.Fragment>);
      });
    });
    return out;
  }, [text]);
  return <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>{nodes}</Typography>;
}

function TypingDots() {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', px: 1.75, py: 1.35 }}>
      {[0, 1, 2].map((i) => (
        <Box key={i} component={motion.span}
          animate={{ y: [0, -4, 0], opacity: [0.35, 1, 0.35] }}
          transition={{ duration: 0.85, repeat: Infinity, delay: i * 0.16, ease: 'easeInOut' }}
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: alpha(theme.palette.text.primary, 0.5), display: 'inline-block' }} />
      ))}
    </Box>
  );
}

const fmtTime = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};

/**
 * Floating, fail-safe HR assistant chat widget.
 * Gated on GET /assistant/status (renders nothing when disabled). Talks to
 * POST /assistant/chat via the quiet aiApi client, sending the {role, content}
 * history each turn. Polished with framer-motion animation + subtle local sounds.
 */
export default function AssistantWidget() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const reduced = useReducedMotion();
  const sound = useSound();

  const [enabled, setEnabled] = useState(null); // null=checking, false=hidden, true=on
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unread, setUnread] = useState(false);
  const [messages, setMessages] = useState([GREETING()]);
  const idRef = useRef(1);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try { const data = await assistantStatus(); if (active) setEnabled(Boolean(data?.enabled)); }
      catch { if (active) setEnabled(false); }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  if (!enabled) return null;

  const nextId = () => (idRef.current += 1);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setError('');
    setInput('');

    const userMessage = { id: nextId(), role: 'user', content: text.slice(0, MAX_CONTENT), at: Date.now() };
    const nextHistory = [...messages, userMessage];
    setMessages(nextHistory);
    sound.play('send');
    setBusy(true);

    try {
      // Send only the role/content shape the backend validates.
      const payload = nextHistory.map(({ role, content }) => ({ role, content }));
      const data = await assistantChat(payload);
      const reply = (data?.reply || '').trim() || "I'm sorry, I didn't catch that. Could you rephrase?";
      setMessages((m) => [...m, { id: nextId(), role: 'assistant', content: reply, at: Date.now() }]);
      sound.play('receive');
      if (!open) setUnread(true);
    } catch {
      setError('Assistant is unavailable right now.');
      sound.play('error');
    } finally {
      setBusy(false);
    }
  };

  const openPanel = () => { setOpen(true); setUnread(false); sound.play('open'); };
  const resetChat = () => { setMessages([GREETING()]); setError(''); idRef.current = 1; };

  const headerBg = dark
    ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`;
  const radius = theme.hr?.radius?.lg || 20;
  const panelTransition = reduced ? { duration: 0.01 } : { type: 'spring', stiffness: 380, damping: 30, mass: 0.7 };

  return (
    <>
      {/* ---- Floating launcher ---- */}
      <AnimatePresence>
        {!open && (
          <Box component={motion.div}
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            transition={reduced ? { duration: 0.01 } : { type: 'spring', stiffness: 500, damping: 26 }}
            whileHover={reduced ? undefined : { scale: 1.06 }} whileTap={{ scale: 0.92 }}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
            <Tooltip title="HR Assistant" placement="left" arrow>
              <Box sx={{ position: 'relative', width: 60, height: 60 }}>
                {!reduced && (
                  <Box component={motion.span}
                    animate={{ scale: [1, 1.45], opacity: [0.45, 0] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                    sx={{ position: 'absolute', inset: 0, borderRadius: '50%', background: headerBg }} />
                )}
                <Box onClick={openPanel} role="button" aria-label="Open HR assistant chat"
                  sx={{ position: 'relative', width: 60, height: 60, borderRadius: '50%', display: 'grid', placeItems: 'center',
                    cursor: 'pointer', color: '#fff', background: headerBg, boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.5)}` }}>
                  <Box component={motion.div}
                    animate={reduced ? undefined : { rotate: [0, -8, 8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
                    sx={{ display: 'grid', placeItems: 'center' }}>
                    <SupportAgentRoundedIcon />
                  </Box>
                  {unread && (
                    <Box sx={{ position: 'absolute', top: 6, right: 6, width: 14, height: 14, borderRadius: '50%', bgcolor: theme.palette.error.main, border: '2px solid #fff' }} />
                  )}
                </Box>
              </Box>
            </Tooltip>
          </Box>
        )}
      </AnimatePresence>

      {/* ---- Chat panel ---- */}
      <AnimatePresence>
        {open && (
          <Box component={motion.div}
            initial={{ opacity: 0, scale: 0.9, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 24 }}
            transition={panelTransition} style={{ transformOrigin: 'bottom right' }}
            sx={{ position: 'fixed', zIndex: 1300, bottom: { xs: 0, sm: 24 }, right: { xs: 0, sm: 24 },
              width: { xs: '100vw', sm: 392 }, height: { xs: '100dvh', sm: 588 }, maxHeight: { xs: '100dvh', sm: '82vh' } }}>
            <Paper elevation={0} role="dialog" aria-label="HR assistant chat"
              sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                borderRadius: { xs: 0, sm: `${radius}px` }, border: '1px solid', borderColor: alpha(theme.palette.common.white, dark ? 0.08 : 0.6),
                backgroundColor: alpha(theme.palette.background.paper, dark ? 0.85 : 0.92),
                backdropFilter: 'blur(18px) saturate(140%)', boxShadow: `0 24px 70px ${alpha('#000', dark ? 0.6 : 0.28)}` }}>

              {/* header */}
              <Box sx={{ px: 2, py: 1.35, background: headerBg, color: '#fff', display: 'flex', alignItems: 'center', gap: 1.25, position: 'relative', overflow: 'hidden' }}>
                {!reduced && (
                  <Box component={motion.span} animate={{ x: ['-40%', '140%'] }} transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
                    sx={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.12)}, transparent)` }} />
                )}
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: alpha('#fff', 0.2), display: 'grid', placeItems: 'center' }}>
                    <SupportAgentRoundedIcon fontSize="small" />
                  </Box>
                  <Box component={motion.span}
                    animate={reduced ? undefined : { scale: [1, 1.35, 1], opacity: [1, 0.6, 1] }} transition={{ duration: 2, repeat: Infinity }}
                    sx={{ position: 'absolute', bottom: 0, right: 0, width: 11, height: 11, borderRadius: '50%', bgcolor: '#34d399', border: '2px solid', borderColor: theme.palette.primary.main }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0, zIndex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>HR Assistant</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{busy ? 'typing…' : 'Online · here to help'}</Typography>
                </Box>
                <Tooltip title={sound.muted ? 'Sound off' : 'Sound on'} arrow>
                  <IconButton size="small" onClick={sound.toggle} sx={{ color: '#fff', zIndex: 1 }} aria-label="Toggle sound">
                    {sound.muted ? <VolumeOffRoundedIcon fontSize="small" /> : <VolumeUpRoundedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="New chat" arrow>
                  <IconButton size="small" onClick={resetChat} sx={{ color: '#fff', zIndex: 1 }} aria-label="Reset chat">
                    <RestartAltRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" arrow>
                  <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff', zIndex: 1 }} aria-label="Close HR assistant chat">
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* messages */}
              <Box ref={scrollRef} aria-live="polite" sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, py: 1.5,
                '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { borderRadius: 3, backgroundColor: alpha(theme.palette.text.primary, 0.18) } }}>
                <AnimatePresence initial={false}>
                  {messages.map((m) => {
                    const isUser = m.role === 'user';
                    return (
                      <Box key={m.id} component={motion.div} layout={!reduced}
                        initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: reduced ? 0.01 : 0.26, ease: EASE }}
                        sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.25 }}>
                        <Box sx={{ maxWidth: '86%' }}>
                          <Box sx={{ px: 1.75, py: 1.1, borderRadius: 3, color: isUser ? '#fff' : 'text.primary',
                            background: isUser ? headerBg : alpha(theme.palette.text.primary, dark ? 0.09 : 0.05),
                            borderTopRightRadius: isUser ? 6 : 18, borderTopLeftRadius: isUser ? 18 : 6,
                            boxShadow: isUser ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}` : 'none' }}>
                            <RichText text={m.content} />
                          </Box>
                          {m.at && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.35, px: 0.5, fontSize: 10, color: 'text.disabled', textAlign: isUser ? 'right' : 'left' }}>
                              {fmtTime(m.at)}
                            </Typography>
                          )}
                          {!isUser && (m.chips || []).length > 0 && (
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                              {m.chips.map((s, si) => (
                                <Box key={si} component={motion.div}
                                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduced ? 0 : 0.05 * si, duration: 0.2 }}
                                  whileHover={reduced ? undefined : { y: -2 }}>
                                  <Chip label={s} size="small" clickable onClick={() => send(s)} variant="outlined"
                                    sx={{ cursor: 'pointer', fontWeight: 600, borderColor: alpha(theme.palette.primary.main, 0.4),
                                      color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.14) } }} />
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </AnimatePresence>

                <AnimatePresence>
                  {busy && (
                    <Box component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                      <Box sx={{ borderRadius: 3, borderTopLeftRadius: 6, backgroundColor: alpha(theme.palette.text.primary, dark ? 0.09 : 0.05) }}>
                        <TypingDots />
                      </Box>
                    </Box>
                  )}
                </AnimatePresence>

                {error && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                    <Typography variant="caption" color="error" sx={{ textAlign: 'center' }}>{error}</Typography>
                  </Box>
                )}
              </Box>

              {/* input */}
              <Box sx={{ p: 1.1, pt: 0.75, display: 'flex', gap: 1, alignItems: 'flex-end', borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.7) }}>
                <TextField inputRef={inputRef} fullWidth size="small" multiline maxRows={3}
                  placeholder="Ask about leave, policies, requests…"
                  value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  disabled={busy} inputProps={{ maxLength: MAX_CONTENT, 'aria-label': 'Message to HR assistant' }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: alpha(theme.palette.text.primary, 0.04) } }} />
                <Box component={motion.button}
                  whileTap={{ scale: 0.88 }} whileHover={(busy || !input.trim()) ? undefined : { scale: 1.06 }}
                  onClick={() => send()} disabled={busy || !input.trim()} aria-label="Send message"
                  sx={{ flexShrink: 0, width: 42, height: 42, borderRadius: '50%', border: 0, display: 'grid', placeItems: 'center', color: '#fff',
                    cursor: (busy || !input.trim()) ? 'default' : 'pointer',
                    background: (busy || !input.trim()) ? alpha(theme.palette.text.primary, 0.2) : headerBg,
                    boxShadow: (busy || !input.trim()) ? 'none' : `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`, transition: 'background .2s' }}>
                  <SendRoundedIcon fontSize="small" />
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
}
